// Browser speech is best with headphones. Echo filtering is heuristic, not acoustic cancellation.
import { NaturalVoicePlayer } from './naturalVoice';
const normalize = value => value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

export class BrowserCallAudio {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.active = false;
    this.muted = false;
    this.blocked = false;
    this.recognition = null;
    this.queue = [];
    this.speaking = false;
    this.replyToken = 0;
    this.output = '';
    this.delivered = '';
    this.finalParts = [];
    this.interim = '';
    this.networkErrors = 0;
    this.lang = 'en-US';
    this.player = new NaturalVoicePlayer({
      getCallId: () => this.callbacks.getCallId(),
      onPreparing: value => this.callbacks.onPreparing(value),
      onSpeaking: value => { this.speaking = value; if (!value) this.lastOutputAt = Date.now(); this.callbacks.onSpeaking(value); },
      onProgress: text => this.callbacks.onSpokenProgress(text),
      onError: message => this.callbacks.onError(message, 'speaker'),
    });
  }

  start() {
    this.active = true;
    this.blocked = false;
    this.muted = false;
    this.networkErrors = 0;
    this.player.start();
    this.beginRecognition();
  }

  beginRecognition() {
    if (!this.active || this.muted || this.blocked || this.recognition) return;
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      this.blocked = true;
      this.callbacks.onError('This browser doesn’t support voice input. Type below, or use Chrome for a hands-free call.', 'microphone');
      return;
    }
    const recognition = new Recognition();
    this.recognition = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = this.lang;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      if (!this.active || this.recognition !== recognition) return;
      this.callbacks.onMicrophone(true);
    };
    recognition.onresult = event => {
      if (!this.active || this.muted || this.recognition !== recognition) return;
      for (let index = event.resultIndex; index < event.results.length; index++) {
        const result = event.results[index];
        const text = result[0].transcript.trim();
        if (!text || this.isEcho(text)) continue;
        this.networkErrors = 0;
        if (this.callbacks.isReplying()) this.callbacks.onInterrupt();
        if (result.isFinal) { this.finalParts.push(text); this.interim = ''; }
        else this.interim = text;
      }
      const draft = [...this.finalParts, this.interim].filter(Boolean).join(' ');
      if (!draft) return;
      this.callbacks.onDraft(draft);
      clearTimeout(this.finalTimer);
      this.finalTimer = setTimeout(() => this.flushSpeech(), this.interim ? 1500 : 450);
    };
    recognition.onerror = event => {
      if (!this.active || this.recognition !== recognition || event.error === 'aborted' || event.error === 'no-speech') return;
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        this.blocked = true;
        this.callbacks.onError('Microphone permission is needed for a hands-free call. Allow it in your browser, then choose “Retry microphone”—or type below.', 'microphone');
      } else if (event.error === 'audio-capture') {
        this.blocked = true;
        this.callbacks.onError('No available microphone was found. Connect a microphone and retry, or type below.', 'microphone');
      } else {
        this.networkErrors += 1;
        if (this.networkErrors >= 3) {
          this.blocked = true;
          this.callbacks.onError('Your browser’s speech service is unavailable. You can continue by typing below.', 'microphone');
        }
      }
    };
    recognition.onend = () => {
      if (this.recognition !== recognition) return;
      this.recognition = null;
      this.callbacks.onMicrophone(false);
      if (this.active && !this.muted && !this.blocked) {
        clearTimeout(this.restartTimer);
        this.restartTimer = setTimeout(() => this.beginRecognition(), this.networkErrors ? 1000 : 180);
      }
    };
    try { recognition.start(); }
    catch {
      this.recognition = null;
      this.blocked = true;
      this.callbacks.onError('The microphone could not start. Retry microphone or continue by typing.', 'microphone');
    }
  }

  setLanguage(locale) {
    if (this.lang === locale) return;
    this.lang = locale;
    if (!this.recognition) return;
    // Restart recognition so the browser listens in the new language.
    const recognition = this.recognition;
    this.recognition = null;
    clearTimeout(this.finalTimer);
    this.finalParts = []; this.interim = '';
    recognition.abort();
    if (this.active && !this.muted && !this.blocked) this.beginRecognition();
  }

  isEcho(text) {
    if (!this.speaking && Date.now() - (this.lastOutputAt || 0) > 1600) return false;
    const candidate = normalize(text);
    const output = normalize(this.output);
    if (/^(stop|wait|hold on|excuse me|actually|no |please stop)/.test(candidate)) return false;
    if (!output || !candidate) return false;
    if (output.includes(candidate)) return true;
    const words = candidate.split(' ');
    const spokenWords = new Set(output.split(' '));
    return words.length >= 4 && words.filter(word => spokenWords.has(word)).length / words.length >= 0.88;
  }

  flushSpeech() {
    clearTimeout(this.finalTimer);
    const text = [...this.finalParts, this.interim].filter(Boolean).join(' ').trim();
    this.finalParts = []; this.interim = '';
    if (this.active && !this.muted && text) this.callbacks.onFinal(text.slice(0, 2000));
  }

  discardDraft() {
    clearTimeout(this.finalTimer);
    this.finalParts = []; this.interim = '';
  }

  setMuted(muted) {
    this.muted = muted;
    clearTimeout(this.finalTimer); clearTimeout(this.restartTimer);
    this.finalParts = []; this.interim = '';
    if (muted) {
      const recognition = this.recognition;
      this.recognition = null;
      recognition?.abort();
      this.callbacks.onMicrophone(false);
    } else {
      this.blocked = false;
      this.callbacks.onMicrophone(false);
      this.beginRecognition();
    }
  }

  beginReply() {
    this.cancelReply();
    this.output = ''; this.delivered = '';
  }

  enqueue(text) {
    if (!this.active || !text.trim()) return;
    const clean = text.replace(/[*#]/g, '').trim();
    this.output = `${this.output} ${clean}`.trim();
    this.player.enqueue(clean);
  }

  unlockVoice() { this.player.unlock(); }

  cancelReply() {
    this.replyToken += 1;
    this.queue = [];
    clearTimeout(this.speechTimer);
    this.lastOutputAt = Date.now();
    this.player.cancel();
    this.speaking = false;
    this.utterance = null;
    this.callbacks.onSpeaking(false);
  }

  stop() {
    this.active = false;
    clearTimeout(this.restartTimer); clearTimeout(this.finalTimer);
    this.finalParts = []; this.interim = '';
    const recognition = this.recognition;
    this.recognition = null;
    recognition?.abort();
    this.cancelReply();
    this.player.stop();
    this.callbacks.onMicrophone(false);
  }
}